import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag, Trash2, Pencil } from "lucide-react";
import BrandLockup from "@/components/BrandLockup";
import HeaderUtils from "@/components/HeaderUtils";
import PrimaryNav from "@/components/PrimaryNav";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import RacePlanBody from "@/components/race/RacePlanBody";
import { raceNumbers, fmtMin } from "@/components/race/RaceBlocks";
import api from "@/lib/api";
import { toast } from "sonner";

function parseLocal(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function addDaysISO(iso, n) {
  const d = parseLocal(iso); d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Find the athlete's active race across all their plans. Precedence:
//   1. raceWeek.enabled with an explicit dayIndex
//   2. any session with intensity=race or isRace=true
function findActiveRace(plans) {
  const today = new Date().toISOString().slice(0, 10);
  const candidates = [];
  (plans || []).forEach((p) => {
    if (!p?.weekStart) return;
    const tw = p.trainingWeek || {};
    let dayIdx = null;
    let session = null;
    if (p.raceWeek?.enabled && Number.isInteger(p.raceWeek.dayIndex)) {
      dayIdx = p.raceWeek.dayIndex;
      session = (tw.days?.[dayIdx]?.sessions || [])[0] || null;
    } else {
      (tw.days || []).forEach((d, i) => {
        (d.sessions || []).forEach((s) => {
          if (s.isRace || (s.intensity || "").toLowerCase() === "race") {
            if (dayIdx == null) { dayIdx = i; session = s; }
          }
        });
      });
    }
    if (dayIdx == null) return;
    candidates.push({ plan: p, raceIso: addDaysISO(p.weekStart, dayIdx), dayIndex: dayIdx, session,
      weightKg: p.profile?.weight || null });
  });
  if (!candidates.length) return null;
  const future = candidates.filter((c) => c.raceIso >= today).sort((a, b) => a.raceIso.localeCompare(b.raceIso));
  if (future.length) return future[0];
  return candidates.sort((a, b) => b.raceIso.localeCompare(a.raceIso))[0];
}

// Race Hub — the subscriber's race home. Renders the identical race plan that
// one-off Race Pack buyers get (shared RacePlanBody).
export default function RaceHub() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [racePlans, setRacePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(() => localStorage.getItem("spc-race-start-time") || "08:00");
  const [raceMins, setRaceMins] = useState(() => localStorage.getItem("spc-race-expected-mins") || "");

  useEffect(() => {
    (async () => {
      try {
        // Weekly plans (for the race flagged inside the athlete's own plan) plus
        // race-only plans — one-off Race Packs and anything their coach built.
        const [weekly, raceOnly] = await Promise.all([
          api.get("/plans"),
          api.get("/race-pack/plans").catch(() => ({ data: [] })),
        ]);
        const w = Array.isArray(weekly.data) ? weekly.data : (weekly.data?.plans || []);
        setPlans(w);
        setRacePlans((raceOnly.data || []).filter((p) => !p.expired));
      } catch {
        toast.error("Couldn't load your plans");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const race = useMemo(
    () => findActiveRace([...racePlans, ...plans]), [plans, racePlans]);
  const raceName = race?.plan?.raceWeek?.event || race?.plan?.raceWeek?.name
    || (race?.plan?.raceOnly ? race?.plan?.name : null)
    || race?.session?.tpTitle || "Race Day";
  const distance = race?.session?.tpDistanceMi ? `${race.session.tpDistanceMi} mi` : null;
  const daily = race?.plan?.generated?.targets?.daily || [];
  const fuelling = race?.plan?.generated?.fuelling || [];
  const raceFuelSession = ((fuelling[race?.dayIndex] || {}).sessions || [])[0] || null;

  const deleteRacePlan = async (p) => {
    if (!window.confirm(`Delete "${p.name || "this race plan"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/race-pack/plans/${p.id}`);
      setRacePlans((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Race plan deleted");
    } catch {
      toast.error("Could not delete that race plan");
    }
  };

  const saveStart = (v) => {
    setStartTime(v);
    try { localStorage.setItem("spc-race-start-time", v); } catch { /* quota */ }
  };
  const saveMins = (v) => {
    setRaceMins(v);
    try { localStorage.setItem("spc-race-expected-mins", v); } catch { /* quota */ }
  };

  const n = useMemo(() => raceNumbers({
    durMin: Number(raceMins) || race?.session?.duration || raceFuelSession?.duration || 0,
    weightKg: race?.weightKg || 0,
    carbsPerHour: race?.plan?.raceWeek?.carbsPerHour,
    heat: !!(race?.session?.heat || race?.plan?.raceWeek?.heat),
    fuelling: race?.plan?.raceWeek?.fuelling || {},
    sport: race?.session?.sport || raceFuelSession?.sport || "",
  }), [race, raceFuelSession, raceMins]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Logo className="h-10 opacity-40 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24 race-print-root">
      <header className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur sticky top-0 z-30 no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav plans={plans} />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {!race ? (
          <div className="border border-dashed border-white/15 p-10 text-center" data-testid="race-hub-empty">
            <Flag className="h-8 w-8 text-zinc-500 mx-auto mb-3" />
            <p className="text-lg font-display uppercase text-white">No race set</p>
            <p className="text-sm text-zinc-400 max-w-md mx-auto mt-2">
              Flag a session in your Plan as a Race (Step 5 · Training) and every race-day tool will unlock here.
            </p>
            <Button onClick={() => navigate("/app")}
              className="mt-4 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold uppercase tracking-wider text-xs">
              Open plan builder
            </Button>
          </div>
        ) : (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">
                Race fuelling plan · {fmtMin(n.durMin)} ·{" "}
                {(race.session?.heat || race.plan?.raceWeek?.heat) ? "hot conditions" : "mild conditions"}
              </p>
              <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter font-black mt-2">{raceName}</h1>
              {race?.plan?.raceOnly && !race?.plan?.coachBuilt && (
                <button type="button" data-testid="race-hub-edit-plan"
                  onClick={() => navigate(`/race-pack/${race.plan.id}/edit`)}
                  className="mt-3 inline-flex items-center gap-2 border border-[#E2E30A]/60 text-[#E2E30A] hover:bg-[#E2E30A] hover:text-black px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors no-print">
                  <Pencil className="h-3.5 w-3.5" /> Edit this race plan
                </button>
              )}
            </div>

            <RacePlanBody
              name={raceName} raceIso={race.raceIso} planId={race.plan?.id}
              startTime={startTime} onStartTime={saveStart}
              raceMins={raceMins} onRaceMins={saveMins}
              n={n} weightKg={race.weightKg || 0}
              daily={daily} fuelling={fuelling} raceIdx={race.dayIndex}
              strategy={race.plan?.raceWeek?.strategy} sport={race.session?.sport}
              distance={distance}
            />

            {racePlans.some((p) => p.id !== race.plan?.id) && (
              <section className="border border-white/10 bg-[#141414] p-4 no-print" data-testid="race-other-plans">
                <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">
                  Your other race plans
                </p>
                <div className="mt-3 space-y-1.5">
                  {racePlans.filter((p) => p.id !== race.plan?.id).map((p) => (
                    <div key={p.id} className="flex items-center gap-1 border border-white/10 hover:border-[#E2E30A]/50 hover:bg-[#E2E30A]/[0.04] transition-colors">
                      <button type="button" data-testid={`race-other-plan-${p.id}`}
                        onClick={() => navigate(`/race-pack/${p.id}`)}
                        className="flex-1 min-w-0 flex items-center gap-3 text-left px-3 py-2">
                        <Flag className="h-3.5 w-3.5 text-[#E2E30A] shrink-0" />
                        <span className="flex-1 min-w-0 text-[13px] text-white truncate">{p.name}</span>
                        {p.coachBuilt && (
                          <span className="text-[9px] uppercase tracking-widest text-[#34C759] border border-[#34C759]/40 px-1.5 py-0.5 shrink-0">
                            From your coach
                          </span>
                        )}
                        <span className="text-[11px] text-zinc-400 shrink-0">{p.raceDate}</span>
                      </button>
                      {!p.coachBuilt && (
                        <button type="button" data-testid={`race-other-plan-edit-${p.id}`}
                          onClick={() => navigate(`/race-pack/${p.id}/edit`)} aria-label={`Edit ${p.name}`}
                          className="shrink-0 grid place-items-center h-8 w-8 text-zinc-400 hover:text-[#E2E30A] hover:bg-[#E2E30A]/10 transition-colors">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button type="button" data-testid={`race-other-plan-delete-${p.id}`}
                        onClick={() => deleteRacePlan(p)} aria-label={`Delete ${p.name}`}
                        className="shrink-0 grid place-items-center h-8 w-8 mr-1 text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Named helper export for the Tracker shortcut card.
export { findActiveRace };
